import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarCheck, Check, Clock, Package, Wallet } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionTag } from "@/components/SectionTag";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTABlock } from "@/components/CTABlock";
import { OFFERS } from "@/data/offers";
import { OFFERS_EN } from "@/data/offers.en";
import { SITE } from "@/constants/site";
import { useLang } from "@/i18n/LangContext";

export default function OfferDetail() {
  const { slug } = useParams();
  const { t, l, lang } = useLang();
  const offers = lang === "en" ? OFFERS_EN : OFFERS;
  const offer = offers.find((o) => o.slug === slug);
  const o = t.offerPage;
  if (!offer) return <Navigate to={l("/services")} replace />;
  const index = offers.indexOf(offer);
  const others = offers.filter((x) => x.slug !== slug);
  const frSlug = offer.frSlug || offer.slug;

  return (
    <>
      <Seo
        title={offer.seo.title}
        description={offer.seo.description}
        path={`/services/${frSlug}`}
        schemas={[
          breadcrumbSchema([
            { name: t.common.home, path: "/" },
            { name: o.servicesBreadcrumb, path: "/services" },
            { name: offer.name, path: `/services/${frSlug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: offer.name,
            serviceType: offer.serviceType,
            description: offer.summary,
            provider: {
              "@type": "ProfessionalService",
              "@id": "https://www.magaliclouzet.com/#organization",
              name: `Magali Clouzet — ${t.role}`,
            },
            areaServed: ["Alpes-Maritimes", "Nice", "Cannes", "Sophia Antipolis", "Antibes", "Monaco", "France"],
          },
        ]}
      />

      <section className="px-5 md:px-10 pt-36 md:pt-44 pb-16 md:pb-24" data-testid="offer-hero">
        <div className="mx-auto max-w-7xl">
          <Breadcrumb
            items={[
              { label: t.common.home, to: "/" },
              { label: o.servicesBreadcrumb, to: "/services" },
              { label: offer.name },
            ]}
          />
          <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <SectionTag>{`${t.common.offerBadge} ${String(index + 1).padStart(2, "0")}`}</SectionTag>
              <Reveal delay={0.1}>
                <h1 className="mt-5 font-display text-4xl md:text-6xl font-extrabold tracking-tighter text-balance">
                  {offer.name}
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-7 text-base md:text-lg leading-relaxed text-plum/85">{offer.subtitle || offer.short}</p>
              </Reveal>
              <Reveal delay={0.3} className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-sand px-4 py-2 text-sm font-medium text-plum">
                  <Clock className="w-4 h-4 text-terracotta-deep" /> {offer.duration}
                </span>
                {offer.engagement && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-sand px-4 py-2 text-sm font-medium text-plum">
                    {offer.engagement}
                  </span>
                )}
                <span className="inline-flex items-center gap-2 rounded-full bg-sand px-4 py-2 text-sm font-medium text-plum">
                  <Wallet className="w-4 h-4 text-terracotta-deep" /> {offer.price}
                </span>
              </Reveal>
              <Reveal delay={0.4} className="mt-10 flex flex-wrap gap-4">
                <a
                  href={SITE.calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="offer-cta-rdv"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-plum"
                >
                  <CalendarCheck className="w-5 h-5 text-terracotta" />
                  {t.common.bookCall}
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <Link
                  to={l("/contact")}
                  data-testid="offer-cta-devis"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/60 px-7 py-4 text-sm font-semibold transition-colors duration-300 hover:bg-white"
                >
                  {t.common.requestQuote}
                </Link>
              </Reveal>
            </div>
            <Reveal delay={0.25}>
              <div className="overflow-hidden rounded-[2.5rem] shadow-[0_40px_80px_-20px_rgba(17,14,13,0.2)]">
                <img src={offer.image} alt={offer.imageAlt} className="h-80 lg:h-[26rem] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-16 md:py-24 bg-white" data-testid="offer-summary">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <div className="rounded-3xl border-l-4 border-terracotta bg-cream/70 p-8 md:p-10">
              <h2 className="font-display text-lg font-bold tracking-tight text-terracotta-deep">{o.summaryTitle}</h2>
              <p className="mt-3 text-base md:text-lg leading-relaxed text-plum/90">{offer.summary}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{o.audienceTitle}</h2>
            <p className="mt-4 text-base leading-relaxed text-plum/85">{offer.audience}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 md:px-10 py-16 md:py-24" data-testid="offer-included">
        <div className="mx-auto max-w-7xl grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionTag>{o.includedTag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-2xl md:text-4xl font-bold tracking-tight text-balance">
                {o.includedH2}
              </h2>
            </Reveal>
            <ul className="mt-10 space-y-4">
              {offer.included.map((item, i) => (
                <Reveal key={item} delay={i * 0.05}>
                  <li className="flex items-start gap-4 rounded-2xl bg-white p-5 border border-ink/5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-terracotta/20">
                      <Check className="w-4 h-4 text-terracotta-deep" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm md:text-base text-plum/90">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <SectionTag>{o.processTag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-2xl md:text-4xl font-bold tracking-tight text-balance">
                {offer.processH2 || o.processH2}
              </h2>
            </Reveal>
            <ol className="mt-10 space-y-0">
              {offer.process.map((step, i) => (
                <Reveal key={step.title} delay={i * 0.06}>
                  <li className="relative flex gap-6 pb-10 last:pb-0">
                    {i < offer.process.length - 1 && (
                      <span className="absolute left-[1.15rem] top-12 h-full w-px bg-terracotta/30" aria-hidden="true" />
                    )}
                    <span className="z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink font-display text-sm font-bold text-cream">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold tracking-tight">{step.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-plum/75">{step.text}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-16 md:py-24 bg-sand/50" data-testid="offer-deliverables">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <SectionTag>{o.deliverablesTag}</SectionTag>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-2xl md:text-4xl font-bold tracking-tight text-balance">
                {o.deliverablesH2}
              </h2>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {offer.deliverables.map((d, i) => (
              <Reveal key={d} delay={i * 0.08}>
                <div className="h-full rounded-3xl bg-white p-7 border border-ink/5 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-15px_rgba(17,14,13,0.12)]">
                  <Package className="w-6 h-6 text-terracotta-deep" strokeWidth={1.75} aria-hidden="true" />
                  <p className="mt-4 text-sm font-semibold leading-snug">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 md:px-10 py-16 md:py-24" data-testid="offer-others">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{o.othersTitle}</h2>
          </Reveal>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {others.map((other) => (
              <Reveal key={other.slug}>
                <Link
                  to={l(`/services/${other.frSlug || other.slug}`)}
                  data-testid={`offer-other-${other.frSlug || other.slug}`}
                  className="group flex items-center justify-between gap-6 rounded-3xl border border-ink/5 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-15px_rgba(17,14,13,0.12)]"
                >
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{other.name}</h3>
                    <p className="mt-1.5 text-sm text-plum/70">{other.duration} · {other.price}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 shrink-0 text-terracotta-deep transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABlock
        title={offer.ctaTitle || t.common.cta.title}
        text={offer.ctaText || o.ctaDefaultText}
      />
    </>
  );
}
